import React, { forwardRef } from 'react';
import {
  MagnifyingGlassIcon,
  EnvelopeIcon,
  UserCircleIcon,
  LockClosedIcon,
  PhoneIcon,
  MapPinIcon,
  CalendarIcon,
  ClockIcon,
  DocumentTextIcon,
  TagIcon,
  LinkIcon,
  AtSymbolIcon,
  KeyIcon,
  HomeIcon,
  BuildingOfficeIcon,
  GlobeAltIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

// Icon mapping
const iconMap = {
  search: MagnifyingGlassIcon,
  email: EnvelopeIcon,
  user: UserCircleIcon,
  lock: LockClosedIcon,
  phone: PhoneIcon,
  location: MapPinIcon,
  calendar: CalendarIcon,
  clock: ClockIcon,
  document: DocumentTextIcon,
  tag: TagIcon,
  link: LinkIcon,
  at: AtSymbolIcon,
  key: KeyIcon,
  home: HomeIcon,
  building: BuildingOfficeIcon,
  globe: GlobeAltIcon,
};

// Size configurations
const sizeClasses = {
  small: 'px-2 py-1.5 text-xs',
  medium: 'px-3 py-2 text-sm',
  large: 'px-4 py-3 text-base',
};

// Icon sizes based on input size
const iconSizes = {
  small: 'h-4 w-4',
  medium: 'h-5 w-5',
  large: 'h-5 w-5',
};

// Padding adjustments when icons are present
const paddingWithIcon = {
  small: { left: 'pl-8', right: 'pr-8' },
  medium: { left: 'pl-10', right: 'pr-10' },
  large: { left: 'pl-12', right: 'pr-12' },
};

// Shared base classes
const base = 'w-full rounded-md';
const normalVariant = `${base} border-[0.5px] border-gray-300 dark:border-gray-600 bg-surface dark:bg-gray-870 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500`;
// Glass variant: default (light) styles, with dark: overrides when .dark is present on <html>
const glassVariant = `${base} border-[0.5px] border-gray-300 bg-white/5 text-gray-900 placeholder-gray-400 dark:border-white/20 dark:bg-white/5 dark:text-white/90 dark:placeholder-white/60 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500`;

/**
 * FormInput - Reusable input component with size, variant, icon support, and clear button
 *
 * @param {Object} props
 * @param {string} props.variant - 'normal' | 'glass' (default: 'normal')
 * @param {string} props.size - 'small' | 'medium' | 'large' (default: 'medium')
 * @param {string|React.Component} props.leftIcon - Icon name or component for left side
 * @param {string|React.Component} props.rightIcon - Icon name or component for right side
 * @param {boolean} props.showClearButton - Show clear button when input has value (default: true)
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.type - Input type (default: 'text')
 * @param {string} props.value - Input value
 * @param {function} props.onChange - Change handler
 * @param {React.Ref} ref - Forwarded ref
 *
 * @example
 * <FormInput leftIcon="search" size="large" placeholder="Search..." />
 * <FormInput leftIcon="email" placeholder="Email address" showClearButton={false} />
 * <FormInput rightIcon="lock" type="password" />
 */
const FormInput = forwardRef(function FormInput({
  variant = 'normal',
  size = 'medium',
  leftIcon = null,
  rightIcon = null,
  showClearButton = true,
  className = '',
  type = 'text',
  value,
  onChange,
  ...rest
}, ref) {
  const variantClass = variant === 'glass' ? glassVariant : normalVariant;
  const sizeClass = sizeClasses[size] || sizeClasses.medium;

  // Get icon components - memoized to prevent re-renders
  const LeftIconComponent = React.useMemo(
    () => typeof leftIcon === 'string' ? iconMap[leftIcon] : leftIcon,
    [leftIcon]
  );
  const RightIconComponent = React.useMemo(
    () => typeof rightIcon === 'string' ? iconMap[rightIcon] : rightIcon,
    [rightIcon]
  );

  // Determine if we should show the clear button
  const hasValue = value && value.toString().length > 0;
  const shouldShowClear = showClearButton && hasValue && type === 'text';

  // Handle clear button click
  const handleClear = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (onChange) {
      // Create a synthetic event to pass to onChange
      const syntheticEvent = {
        target: { value: '' },
        currentTarget: { value: '' },
      };
      onChange(syntheticEvent);
    }
  }, [onChange]);

  // Adjust padding for icons and clear button - memoized
  const paddingClass = React.useMemo(() => {
    let padding = '';
    if (LeftIconComponent) {
      padding += paddingWithIcon[size].left + ' ';
    }
    if (RightIconComponent || shouldShowClear) {
      padding += paddingWithIcon[size].right + ' ';
    }
    return padding;
  }, [LeftIconComponent, RightIconComponent, shouldShowClear, size]);

  const combined = React.useMemo(
    () => `${variantClass} ${sizeClass} ${paddingClass} ${className}`.trim(),
    [variantClass, sizeClass, paddingClass, className]
  );
  const iconSizeClass = iconSizes[size] || iconSizes.medium;

  // Determine if we need a wrapper (for icons or potential clear button)
  const needsWrapper = LeftIconComponent || RightIconComponent || showClearButton;

  // If no wrapper needed at all, return simple input
  if (!needsWrapper) {
    return <input ref={ref} type={type} value={value} onChange={onChange} className={combined} {...rest} />;
  }

  // Always return input with wrapper to prevent re-mounting when clear button appears/disappears
  return (
    <div className="relative">
      {LeftIconComponent && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <LeftIconComponent className={`${iconSizeClass} text-gray-400 dark:text-gray-500`} />
        </div>
      )}
      <input ref={ref} type={type} value={value} onChange={onChange} className={combined} {...rest} />
      {shouldShowClear && (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors focus:outline-none"
          aria-label="Clear input"
        >
          <XMarkIcon className="w-5 h-5" />
        </button>
      )}
      {RightIconComponent && !shouldShowClear && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
          <RightIconComponent className={`${iconSizeClass} text-gray-400 dark:text-gray-500`} />
        </div>
      )}
    </div>
  );
});

export default FormInput;
